import React, { useState, useEffect } from 'react';
import { Container, BottomNavigation, BottomNavigationAction, Box } from '@mui/material';
import HomeIcon from "@mui/icons-material/Home";
import TourIcon from "@mui/icons-material/Event";
import { router, usePage } from '@inertiajs/react';
import theme from '../styles/theme'
import { ThemeProvider } from '@mui/material/styles';
import { Header } from './Header';
import { useMediaQuery } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleIcon from '@mui/icons-material/People';

interface MainLayoutProps {
    children: React.ReactNode;
    headerText?: React.ReactNode;
    headerButton: React.ReactNode;
    currentListingId?: string | null;
    /** When true, show Home + Roomies & Bills. When false/undefined, show Home + Tours + Prospects. */
    utilDashboard?: boolean;
    /** The household ID for util dashboard navigation */
    householdId?: string | null;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, headerText, headerButton, currentListingId, utilDashboard = false, householdId }) => {
    const isDesktop = useMediaQuery('(min-width: 600px)');
    const contentWidth = isDesktop ? '500px' : '97%';
    const { url } = usePage();
    const [currentPage, setCurrentPage] = useState('home');

    useEffect(() => {
        if (utilDashboard) {
            if (url.includes('/bills')) setCurrentPage('bills');
            else if (url.includes('/roomies')) setCurrentPage('roomies');
            else setCurrentPage('home');
            return;
        }
        if (url.includes('/util/dashboard')) {
            setCurrentPage('home');
            return;
        }
        if (url.includes('/tours')) {
            setCurrentPage('tours');
        } else if (url.includes('/listings/') || url === '/home') {
            setCurrentPage('home');
        } else if (url.includes('/prospects')) {
            setCurrentPage('prospects');
        } else {
            setCurrentPage('home');
        }
    }, [url, utilDashboard]);

    const handleHomeClick = () => {
        // Try to get listing ID from props first, then sessionStorage, then default to '/'
        const listingId = currentListingId || sessionStorage.getItem('currentListingId');

        if (listingId) {
            router.visit(`/listings/${listingId}`);
        } else {
            router.visit('/home');
        }
    };

    const bottomNavSx = {
        position: "fixed" as const,
        bottom: 0,
        left: 0,
        right: 0,
        maxWidth: '100%',
        borderTop: '1px solid #eee',
        boxShadow: '0px -2px 10px rgba(0,0,0,0.05)'
    };

    const renderUtilDashboardNav = () => (
        <BottomNavigation showLabels value={currentPage} sx={bottomNavSx}>
            <BottomNavigationAction
                label="Home"
                icon={<HomeIcon />}
                onClick={() => router.visit(`/util/dashboard/${householdId}`)}
                value="home"
            />
            <BottomNavigationAction
                label="Bills"
                icon={<ReceiptLongIcon />}
                onClick={() => router.visit(`/util/dashboard/${householdId}/bills`)}
                value="bills"
            />
            <BottomNavigationAction
                label="Roomies"
                icon={<PeopleIcon />}
                onClick={() => router.visit(`/util/dashboard/${householdId}/roomies`)}
                value="roomies"
            />
        </BottomNavigation>
    );

    const renderHomeNav = () => (
        <BottomNavigation showLabels value={currentPage} sx={bottomNavSx}>
            <BottomNavigationAction
                label="Home"
                icon={<HomeIcon />}
                onClick={handleHomeClick}
                value="home"
            />
            <BottomNavigationAction
                label="Tours"
                icon={<TourIcon />}
                onClick={() => router.visit('/home/tours')}
                value="tours"
            />
            <BottomNavigationAction
                label="Prospects"
                icon={<ChatBubbleOutlineIcon />}
                onClick={() => router.visit('/home/prospects')}
                value="prospects"
            />
        </BottomNavigation>
    );

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={false} sx={{ p: 0, mt: 0, ml: 0, mr: 0, mb: 10, width: '100%', display: 'flex', flexDirection: 'column', minHeight: '100vh', alignItems: 'center' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: contentWidth, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Header headerText={headerText} headerButton={headerButton} />
                        {children}
                    </Box>
                </Box>
                {utilDashboard ? renderUtilDashboardNav() : renderHomeNav()}
            </Container>
        </ThemeProvider>
    );
};

export default MainLayout;